import { getMerchantTz } from '@lib/merchant-tz';
import type { Formatters } from '@resource-kit/core';
import {
  formatCurrency,
  formatDate,
  formatDateTimeCompact,
  formatDatetimeSince,
  formatDuration,
  formatNumber,
} from '@lib/utils';

/**
 * This app's `Formatters` seam member, alone in its own module (#2169 PR-2).
 *
 * Split out of `resourceKitServices.tsx` so a caller that needs ONLY the
 * formatters — the CSV/print export chain's `{ t, format }` context, and the
 * tests that build one — can take them without also pulling in the provider's
 * toast, confirm and auth imports. The adapter still names them here and
 * nowhere else, which is the point: one declaration, two readers.
 */
/**
 * The formatters, hoisted out of the provider component: they are host module
 * bindings, so nothing about a render can change what this object should hold.
 *
 * `timeZone` is a GETTER, and that is what keeps this object FROZEN (the
 * FROZEN / BORROWED / REACTIVE split, named in the module header of
 * `lib/resourceKitServices.tsx` under "the members split three ways") while
 * still answering the current zone. It reads
 * `getMerchantTz()` at ACCESS time, which is the same thing the date formatters
 * beside it do inside their own bodies (`src/lib/utils.ts::formatDate` and
 * `src/lib/utils.ts::formatDateTimeCompact`, and
 * `src/lib/datetime.ts::formatDateTime`, `src/lib/datetime.ts::formatTime` and
 * `src/lib/datetime.ts::formatIsoWithOffset` for the ones that delegate there) —
 * same function, same module-level cache, read at the moment of use. So for
 * INSTANT values a kit hook and a host-rendered cell resolve the same zone, and
 * switching merchant timezone does not have to change this object's identity.
 *
 * DATE-ONLY values reach this member on NEITHER side, so nothing here makes
 * the two agree. Host-side, `formatDate` pins them to UTC by VALUE SHAPE so the
 * calendar day survives (`src/lib/datetime.ts::formatDate`) and never reads `timeZone`.
 * Kit-side, a `date` column is never an instant kind
 * (`packages/resource-kit-controllers/query/deriveSchema.ts::deriveColumnTypes`), so the
 * serializer skips bound completion for it and consults no zone at all
 * (`packages/resource-kit-controllers/query/serialize.ts::completeConditionValue`).
 * Whose day a bare-day bound on an INSTANT column names is a third, independent
 * axis — the field's own `instantReading`
 * (`packages/resource-kit-controllers/query/deriveSchema.ts::instantKindOf`) — neither
 * this member nor the value's shape.
 */
export const HOST_FORMATTERS: Formatters = {
  formatDate,
  formatDateTimeCompact,
  formatDatetimeSince,
  formatDuration,
  formatCurrency,
  formatNumber,
  get timeZone() {
    return getMerchantTz();
  },
};
